import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { Form, Formik } from 'formik'
import type { TFunction } from 'i18next'

import { TextFieldInput } from '@dhl/formik'
import { useDownload } from '@dhl/axios'
import { Box, Button, Card, Grid, H6 } from '@dhl-ui/components'
import { useNotificationMessage } from '~/common'

export type InvoiceFormValues = {
  value: {
    invoiceNumber: string
    trackercode: string
    paymentID: string
  }
}

export const InvoicesReportPage = () => {
  const { t } = useTranslation()
  const { showError } = useNotificationMessage()
  const { triggerDownload } = useDownload()

  const initialValues: InvoiceFormValues = useMemo(
    () => ({
      value: {
        invoiceNumber: '',
        trackercode: '',
        paymentID: '',
      },
    }),
    []
  )

  const onSubmit = async (values: InvoiceFormValues) => {
    try {
      await triggerDownload(createInvoicesDownloadUrl(values), 'invoices.pdf')
    } catch (error: any) {
      if (error.response && error.response.data) {
        const errorResponse = JSON.parse(await error.response.data.text())
        showError(errorResponse.message)
      } else {
        showError(t('admin.reportsPage.error'))
      }
    }
  }

  const validationSchema = createInvoicesValidationSchema(t)

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validateOnChange={false}
      enableReinitialize
    >
      {({ isSubmitting }) => (
        <Card>
          <Card.Header title={t('admin.reportsPage.invoices')} />
          <Card.Content>
            <Form data-testid="invoices-form">
              <Grid container spacing={2} direction="row" alignItems="center">
                <Grid size={{ xs: 2 }}>
                  <H6>{t('admin.reportsPage.invoice')}</H6>
                </Grid>
                <Grid size={{ xs: 7 }} pb={1}>
                  <TextFieldInput
                    fullWidth
                    label={t('admin.reportsPage.invoice')}
                    name="value.invoiceNumber"
                    type="number"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} direction="row" alignItems="center">
                <Grid size={{ xs: 2 }}>
                  <H6>{t('admin.reportsPage.trackerCode')}</H6>
                </Grid>
                <Grid size={{ xs: 7 }} pb={1}>
                  <TextFieldInput
                    fullWidth
                    label={t('admin.reportsPage.trackerCode')}
                    name="value.trackercode"
                    type="text"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} direction="row" alignItems="center">
                <Grid size={{ xs: 2 }}>
                  <H6>{t('admin.reportsPage.paymentID')}</H6>
                </Grid>
                <Grid size={{ xs: 7 }} pb={1}>
                  <TextFieldInput
                    fullWidth
                    label={t('admin.reportsPage.paymentID')}
                    name="value.paymentID"
                    type="text"
                  />
                </Grid>
              </Grid>

              <Box marginTop={2} display="flex" justifyContent="flex-end">
                <Button size="medium" disabled={isSubmitting} color="primary" type="submit" variant="contained">
                  {t('admin.reportsPage.downloadInvoice')}
                </Button>
              </Box>
            </Form>
          </Card.Content>
        </Card>
      )}
    </Formik>
  )
}

export const createInvoicesValidationSchema = (t: TFunction) =>
  Yup.object({
    value: Yup.lazy((value) => {
      if (value && Object.values(value).some((v) => !(v === null || v === undefined || v === ''))) {
        // Otherwise, return a simple validation
        return Yup.mixed().notRequired()
      }
      // Return our normal validation
      return Yup.object({
        invoiceNumber: Yup.string().required(t('admin.reportsPage.invoiceIsRequired')),
        trackercode: Yup.string().required(t('admin.reportsPage.trackerCodeIsRequired')),
        paymentID: Yup.string().required(t('admin.reportsPage.paymentIdIsRequired')),
      })
    }),
  }).defined()

export const createInvoicesDownloadUrl = (values: InvoiceFormValues) => {
  return `/api/invoices?trackercode=${values.value.trackercode}&invoicenr=${values.value.invoiceNumber}&billingentity=&pspid=${values.value.paymentID}&parcelshopKey=`
}
